import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { Formik, Form, Field } from "formik";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { ApolloError } from "@apollo/client";
import { captureException } from "@sentry/browser";
import { Link, useLocation } from "react-router-dom";
import hotspringLogo from "@/assets/logo.svg";
import { InputField } from "@/components/common/InputField/InputField";
import { Button } from "@/components/ui/button";
import { FORGOT_PASSWORD_URL, HOME_URL } from "@/helpers/url.ts";
import { getGraphQLURL, HOTSPRING_LINK, TOKEN_KEY } from "@/env.ts";
import { FooterLinks } from "@/pages/auth/FooterLinks.tsx";
import { colors } from "@/theme/colors.ts";

import AuthCoverImage from "@/assets/AuthCover.png";

const LOGIN_MUTATION = gql`
  mutation LoginUser($username: String!, $password: String!) {
    tokenAuth(username: $username, password: $password) {
      token
      payload
      message
      signupToken
    }
  }
`;

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

interface FormValues {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const location = useLocation();
  const [getLoginUser] = useMutation(LOGIN_MUTATION, {
    context: { uri: getGraphQLURL() },
  });

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const handleSuccess = () => {
    window.location.href = (location.state && location.state.next) || HOME_URL;
  };

  return (
    <div className="dark flex w-[100vw] h-[100vh] bg-background">
      <div className="w-[4vw] bg-[#000]" />
      <div className="w-[46vw] h-full">
        <img
          src={AuthCoverImage}
          alt="Cover image"
          className="h-full w-full object-cover"
        />
      </div>
      <div
        className="absolute top-0 left-[25vw] h-full z-11 pointer-events-none"
        style={{
          width: "25vw",
          background:
            "linear-gradient(270deg, #030A11 0%, rgba(3, 10, 17, 0) 100%)",
        }}
      />
      <div className="w-[50vw] h-full bg-background flex flex-col items-center justify-center relative">
        <Formik<FormValues>
          initialValues={{ username: "", password: "" }}
          validationSchema={toFormikValidationSchema(loginSchema)}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setIsAuthenticating(true);
            try {
              const res = await getLoginUser({ variables: values });
              const { data } = res;
              const loginResult = data?.tokenAuth;

              if (loginResult?.signupToken) {
                // If it's a temporary password, redirect to signup page with token
                window.location.href = `/signup?token=${loginResult.signupToken}`;
              } else if (loginResult?.token) {
                localStorage.setItem(TOKEN_KEY, data?.tokenAuth.token);
                handleSuccess();
              } else if (loginResult?.message) {
                // Login failed but backend provides message
                setStatus({ error: loginResult.message });
                setIsAuthenticating(false);
              } else {
                setStatus({
                  error: data?.tokenAuth.payload || "Login failed",
                });
                setIsAuthenticating(false);
              }
            } catch (err) {
              setIsAuthenticating(false);
              captureException(err);
              if (err instanceof ApolloError) {
                const message = err?.graphQLErrors?.[0]?.message;
                setStatus(message);
              } else {
                setStatus("An unexpected error occurred");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <Form className="w-full flex flex-col items-start gap-[26px] px-[22%]">
              <img src={hotspringLogo} alt="Hotspring Logo" />
              <p className="text-sm text-[#F04438]">{status?.error}</p>
              <div className="w-full flex flex-col gap-[24px]">
                <Field
                  name="username"
                  label="Username"
                  component={InputField}
                  placeholder="username"
                  labelClassName="!text-white"
                  inputClassName="!text-primary-foreground h-10 text-white !border-border !bg-primary autofill"
                  iconClassName="!top-[38px]"
                  disabled={isAuthenticating}
                />
                <div className="flex flex-col gap-[6px] items-end">
                  <Field
                    name="password"
                    type="password"
                    label="Password"
                    component={InputField}
                    placeholder="************"
                    labelClassName="!text-white"
                    inputClassName="!text-primary-foreground h-10 !border-border !bg-primary autofill"
                    iconClassName="!top-[38px]"
                    disabled={isAuthenticating}
                  />
                  <Link to={FORGOT_PASSWORD_URL} className="text-service-paint">
                    Forgot password?
                  </Link>
                </div>
              </div>
              <div className="w-full">
                <Button
                  type="submit"
                  style={{ background: colors.button }}
                  className="w-full text-white p-3 rounded !border-white hover:opacity-80 transition disabled:opacity-50"
                  disabled={isAuthenticating}
                >
                  {isSubmitting || isAuthenticating ? "Logging in..." : "Login"}
                </Button>
              </div>
              <div className="dark text-white">
                Don't have an account? Get one&nbsp;
                <a href={HOTSPRING_LINK} className="text-service-paint">
                  here
                </a>
              </div>
            </Form>
          )}
        </Formik>
        <FooterLinks className="absolute right-[22%] bottom-[5%] w-[calc(50vw-44%)]" />
      </div>
    </div>
  );
};

export default Login;
